import { IncomingHttpHeaders } from 'node:http';

/**
 * Checks if the given headers object is an instance of the Headers class.
 * @param headers - The headers object to check.
 * @returns True if the headers object is an instance of Headers, false otherwise.
 */
export function isHeaders(headers?: IncomingHttpHeaders | Headers): headers is Headers {
  return (typeof headers === 'object' && 'get' in headers) ? typeof headers.get === 'function' : false;
}

/**
 * Checks if the given headers object is an instance of IncomingHttpHeaders.
 * @param headers - The headers object to check.
 * @returns True if the headers object is an instance of IncomingHttpHeaders, false otherwise.
 */
export function isIncomingHttpHeaders(headers?: IncomingHttpHeaders | Headers): headers is IncomingHttpHeaders {
  return typeof headers === 'object' && !('get' in headers);
}

/**
 * Retrieves the host from the headers object.
 * @param headers - The headers object.
 * @returns The host value from the headers object, or null if not found.
 */
export function getHost(headers?: IncomingHttpHeaders | Headers): string | null {
  if (isIncomingHttpHeaders(headers)) {
    return headers['x-forwarded-host'] as string || headers.forwarded || headers.host || null;
  } else if (isHeaders(headers)) {
    return headers.get('X-Forwarded-Host') || headers.get('Forwarded') || headers.get('Host') || null;
  } else {
    return null;
  }
}

/**
 * Retrieves the protocol from the headers object.
 * @param headers - The headers object.
 * @returns The protocol value from the headers object, or 'https' if not found.
 */
export function getProto(headers?: IncomingHttpHeaders | Headers): string {
  if (isIncomingHttpHeaders(headers)) {
    return headers['x-forwarded-proto'] as string || 'https';
  } else if (isHeaders(headers)) {
    return headers.get('X-Forwarded-Proto') || 'https';
  } else {
    return 'https';
  }
}

/**
 * Retrieves the origin from the headers object.
 * @param headers - The headers object.
 * @returns The origin value based on the host and protocol from the headers object, or an empty string if not found.
 */
export function getOrigin(headers?: IncomingHttpHeaders | Headers): string {
  const host = getHost(headers);
  if (host) {
    return host.indexOf('localhost') !== -1 ? 'http://' + host : getProto(headers) + '://' + host;
  } else {
    return process.env.NEXT_PUBLIC_URL || '';
  }
}
